import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Wallet, ArrowUpRight } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { Btn, Badge, eur } from "./common";

/**
 * Painel de faturacao / add-ons reutilizavel.
 * Suporta tanto CogniEtica (com add-ons por tenant) como Privaxia (so billing global) - se o
 * endpoint de add-ons nao existir, o painel salta essa seccao graciosamente.
 *
 * Props:
 *   baseUrl: prefixo da API (ex: "/cognietica" ou "/privaxia" ou "/connectors/<slug>")
 *   supportsAddons: boolean, se true tenta carregar /tenants/{slug}/addons
 */
export function BillingPanel({ baseUrl, supportsAddons = true }) {
  const monthNow = new Date().toISOString().slice(0, 7); // YYYY-MM
  const [month, setMonth] = useState(monthNow);
  const [billing, setBilling] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tenantBilling, setTenantBilling] = useState(null);
  const [billingUnavailable, setBillingUnavailable] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [b, t] = await Promise.all([
        api.get(`${baseUrl}/billing`, { params: { month } }).catch((e) => { setBillingUnavailable(true); return { data: null }; }),
        api.get(`${baseUrl}/tenants`).catch(() => ({ data: { tenants: [] } })),
      ]);
      if (b.data) setBillingUnavailable(false);
      setBilling(b.data || null);
      setTenants(t.data?.tenants || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [month, baseUrl]);

  const openTenant = async (slug) => {
    setSelected(slug);
    setAddons([]);
    setTenantBilling(null);
    try {
      const tb = await api.get(`${baseUrl}/tenants/${slug}/billing`, { params: { month } }).catch(() => ({ data: null }));
      setTenantBilling(tb.data);
      if (supportsAddons) {
        const ad = await api.get(`${baseUrl}/tenants/${slug}/addons`, { params: { month } }).catch(() => ({ data: { addons: [] } }));
        setAddons(ad.data?.addons || ad.data?.items || []);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const rows = billing?.tenants || billing?.rows || [];

  return (
    <div data-testid={`billing-${baseUrl.replace(/[^a-z0-9]/gi, "-")}`}>
      {billingUnavailable && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-amber-50 text-amber-700 text-sm" data-testid="billing-unavailable">
          Esta plataforma nao expoe endpoint de faturacao ({baseUrl}/billing). Mostra apenas a lista de tenants e planos. Consulte a documentacao da API remota se pretende ativar este relatorio.
        </div>
      )}
      <div className="flex items-end justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-end gap-3">
          <div>
            <label className="block text-[10px] font-semibold text-muted2 uppercase tracking-wide mb-0.5">Mes</label>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="px-2 py-1.5 border border-tint rounded text-sm" data-testid="billing-month" />
          </div>
          <Btn variant="ghost" onClick={load} testid="billing-refresh"><RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Atualizar</Btn>
        </div>
        {billing && (
          <div className="flex items-center gap-4">
            <div>
              <div className="text-[10px] uppercase font-semibold text-muted2 tracking-wide">MRR (mes)</div>
              <div className="text-xl font-bold tracking-tight text-navy">{eur(billing.total_mrr ?? billing.mrr ?? billing.total ?? 0)}</div>
            </div>
            {(billing.total_overage != null || billing.overage != null) && (
              <div>
                <div className="text-[10px] uppercase font-semibold text-muted2 tracking-wide">Excedente</div>
                <div className="text-xl font-bold tracking-tight text-amber-600">{eur(billing.total_overage ?? billing.overage ?? 0)}</div>
              </div>
            )}
            <div>
              <div className="text-[10px] uppercase font-semibold text-muted2 tracking-wide">Tenants</div>
              <div className="text-xl font-bold tracking-tight text-blue">{rows.length || tenants.length}</div>
            </div>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-1 bg-white border border-tint rounded-xl overflow-hidden">
          <div className="px-3 py-2 border-b border-tint bg-slate-50 text-xs uppercase font-semibold text-muted2 tracking-wide">Tenants</div>
          <div className="max-h-[500px] overflow-y-auto">
            {tenants.length === 0 && <div className="text-sm text-muted2 p-4 text-center">Sem tenants.</div>}
            {tenants.map((t) => (
              <button key={t.slug} onClick={() => openTenant(t.slug)} data-testid={`billing-tenant-${t.slug}`}
                className={`w-full text-left px-3 py-2 border-b border-tint hover:bg-slate-50 text-sm ${selected === t.slug ? "bg-blue-50" : ""}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-navy truncate">{t.name}</span>
                  <Badge value={t.plan_key || t.plan || "-"} />
                </div>
                <div className="text-[10px] text-muted2 font-mono">{t.slug}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="md:col-span-2">
          {!selected ? (
            <div className="border border-dashed border-tint rounded-xl p-8 text-center text-muted2 text-sm">
              Selecione um tenant para ver os detalhes de faturacao{supportsAddons ? " e add-ons" : ""}.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-white border border-tint rounded-xl p-4" data-testid="billing-tenant-detail">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-[10px] uppercase font-semibold text-muted2 tracking-wide">Tenant</div>
                    <div className="text-lg font-bold tracking-tight text-navy">{selected}</div>
                  </div>
                  <Wallet size={20} className="text-blue" />
                </div>
                {tenantBilling ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <div className="text-[10px] uppercase text-muted2 font-semibold tracking-wide">Plano</div>
                      <div className="font-bold text-navy">{tenantBilling.plan_key || tenantBilling.plan || "-"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-muted2 font-semibold tracking-wide">MRR</div>
                      <div className="font-bold text-navy">{eur(tenantBilling.mrr ?? tenantBilling.total_monthly ?? 0)}</div>
                    </div>
                    {tenantBilling.overage != null && (
                      <div>
                        <div className="text-[10px] uppercase text-muted2 font-semibold tracking-wide">Excedente</div>
                        <div className="font-bold text-amber-600">{eur(tenantBilling.overage)}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-[10px] uppercase text-muted2 font-semibold tracking-wide">Total mes</div>
                      <div className="font-bold text-blue">{eur(tenantBilling.total ?? (tenantBilling.mrr || 0) + (tenantBilling.overage || 0))}</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-muted2">A carregar detalhe...</div>
                )}
              </div>

              {supportsAddons && (
                <div className="bg-white border border-tint rounded-xl overflow-hidden" data-testid="billing-addons">
                  <div className="px-3 py-2 border-b border-tint bg-slate-50 text-xs uppercase font-semibold text-muted2 tracking-wide flex items-center gap-2">
                    <ArrowUpRight size={12} /> Add-ons no periodo
                  </div>
                  {addons.length === 0 ? (
                    <div className="text-sm text-muted2 p-4 text-center">Sem add-ons registados.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="border-b border-tint bg-slate-50">
                        <tr>
                          <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-muted2">Descricao</th>
                          <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-muted2 w-20">Qtd</th>
                          <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-muted2 w-24">Preco un</th>
                          <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-muted2 w-24">Total</th>
                          <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-muted2 w-32">Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {addons.map((a, i) => (
                          <tr key={a.id || i} className="border-b border-tint">
                            <td className="px-3 py-1.5">{a.description || a.descricao || a.type || "-"}</td>
                            <td className="px-3 py-1.5">{a.quantity ?? a.qty ?? 1}</td>
                            <td className="px-3 py-1.5">{eur(a.unit_price ?? a.preco_unitario ?? 0)}</td>
                            <td className="px-3 py-1.5 font-bold">{eur(a.total ?? (a.unit_price || 0) * (a.quantity || 1))}</td>
                            <td className="px-3 py-1.5 text-muted2 text-xs">{a.created_at ? new Date(a.created_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
